import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiAlertTriangle, FiPackage } from 'react-icons/fi';
import { productsAPI } from '../../services/api';
import { LOW_STOCK_DISMISS_KEY } from '../../utils/lowStockAlert';

const STOCK_THRESHOLD = 100;
const RECHECK_INTERVAL_MS = 5 * 60 * 1000; // re-show if still low stock after 5 min

const LowStockAlertModal = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkLowStock = async () => {
      try {
        const res = await productsAPI.getStockAlert(STOCK_THRESHOLD);
        const data = res.data?.data || [];
        if (cancelled || data.length === 0) return;

        setProducts(data);

        const dismissedAt = parseInt(sessionStorage.getItem(LOW_STOCK_DISMISS_KEY) || '0', 10);
        const shouldShow = !dismissedAt || (Date.now() - dismissedAt) > RECHECK_INTERVAL_MS;
        if (shouldShow) setIsOpen(true);
      } catch (error) {
        // Silent fail - don't block admin panel usage over the alert check
        console.error('Low stock alert check failed:', error);
      }
    };

    checkLowStock();
    return () => { cancelled = true; };
  }, []);

  const handleClose = () => {
    sessionStorage.setItem(LOW_STOCK_DISMISS_KEY, String(Date.now()));
    setIsOpen(false);
  };

  const handleViewProducts = () => {
    handleClose();
    navigate('/products');
  };

  if (!isOpen || products.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-orange-50 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-full">
              <FiAlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">Low Stock Alert</h3>
              <p className="text-sm text-gray-600">
                {products.length} product{products.length !== 1 ? 's' : ''} below {STOCK_THRESHOLD} pcs
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-orange-100 transition"
          >
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Product list */}
        <div className="overflow-y-auto flex-1 p-5">
          <ul className="divide-y">
            {products.map((product) => (
              <li key={product.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FiPackage className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{product.name}</p>
                    <p className="text-xs text-gray-500">
                      {product.item_code}{product.category_name ? ` · ${product.category_name}` : ''}
                    </p>
                  </div>
                </div>
                <span className={`text-sm font-semibold ${product.total_stock === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                  {product.total_stock} {product.unit || 'pcs'}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            Dismiss
          </button>
          <button
            onClick={handleViewProducts}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition"
          >
            View Products
          </button>
        </div>
      </div>
    </div>
  );
};

export default LowStockAlertModal;
